import React, { useRef, useState, useEffect } from 'react';
// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
import { Box, Button, HStack } from '@chakra-ui/react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';

import './view-photos-swiper.css';

// import required modules
import {
  Pagination,
  FreeMode,
  Navigation,
  Thumbs,
  Zoom,
  Autoplay
} from 'swiper/modules';

/**
 * A specialized component to show a low-res image initially and swap to high-res once loaded.
 */
const ProgressiveImage = ({ lowResSrc, highResSrc, alt }) => {
  const [currentSrc, setCurrentSrc] = useState(lowResSrc);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Reset state when sources change
    setCurrentSrc(lowResSrc);
    setIsLoaded(false);

    const img = new Image();
    img.src = highResSrc;
    img.onload = () => {
      setCurrentSrc(highResSrc);
      setIsLoaded(true);
    };
  }, [lowResSrc, highResSrc]);

  return (
    <Box position="relative" width="100%" height="100%">
      <img
        src={currentSrc}
        alt={alt}
        style={{
          filter: isLoaded ? 'none' : 'blur(4px)',
          transition: 'filter 0.5s ease-in-out',
          width: '100%',
          height: '100%',
          objectFit: 'contain'
        }}
      />
      {!isLoaded && highResSrc !== lowResSrc && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="blackAlpha.700"
          px={6}
          py={3}
          rounded="lg"
          backdropFilter="blur(4px)"
        >
          <Box color="white" fontSize="sm" fontWeight="500" textAlign="center">
            Loading high resolution photo...
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default function ViewPhotosSwiper({
  photos,
  selectedIndex = 0,
  autoPlay = false,
  showZoom = true,
  showNavigation = true
}) {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);

  const defaultModules = showZoom
    ? [Zoom, FreeMode, Navigation, Thumbs, Pagination]
    : [FreeMode, Navigation, Thumbs, Pagination];
  const [modules] = useState(() =>
    autoPlay ? [...defaultModules, Autoplay] : defaultModules
  );

  const swiperRef = useRef(null);
  const handleZoomIn = () => swiperRef.current?.swiper.zoom.in();
  const handleZoomOut = () => swiperRef.current?.swiper.zoom.out();

  return (
    <>
      <Box position="relative" w="100%" className="box-swiper1">
        <Swiper
          ref={swiperRef}
          pagination={{
            dynamicBullets: true
          }}
          zoom={showZoom}
          initialSlide={selectedIndex}
          style={{
            '--swiper-navigation-color': '#ffffff93',
            '--swiper-pagination-color': 'white'
          }}
          autoplay={
            autoPlay ? { delay: 2500, disableOnInteraction: false } : false
          }
          loop={true}
          spaceBetween={10}
          navigation={showNavigation}
          thumbs={{ swiper: thumbsSwiper }}
          modules={modules}
          className="mySwiper1"
        >
          {photos.map((photo, index) => {
            const highRes = photo.split('?')[0];
            return (
              <SwiperSlide key={index}>
                <div className="swiper-zoom-container">
                  <ProgressiveImage
                    lowResSrc={photo}
                    highResSrc={highRes}
                    alt={`Photo ${index + 1}`}
                  />
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Chakra UI Zoom Controls */}
        {showZoom && (
          <HStack
            spacing={1}
            position="absolute"
            p={1}
            bottom={5}
            rounded="xl"
            boxShadow="lg"
            left="50%"
            transform="translateX(-50%)"
            bg="blackAlpha.800"
            backdropFilter="blur(6px)"
            zIndex={1}
          >
            <Button
              variant="ghost"
              size="md"
              color="gray.200"
              onClick={handleZoomOut}
            >
              −
            </Button>
            <Box color="gray.200" px={2}>
              Zoom
            </Box>
            <Button
              variant="ghost"
              size="md"
              color="gray.200"
              onClick={handleZoomIn}
            >
              +
            </Button>
          </HStack>
        )}
      </Box>

      {/* Thumbnails Swiper (uses low-res for speed) - only show if multiple photos */}
      {photos.length > 1 && (
        <Swiper
          onSwiper={setThumbsSwiper}
          loop={true}
          spaceBetween={10}
          slidesPerView="auto"
          freeMode={true}
          watchSlidesProgress={true}
          modules={[FreeMode, Navigation, Thumbs]}
          className="mySwiper2"
          initialSlide={selectedIndex}
        >
          {photos.map((photo, index) => (
            <SwiperSlide key={index} style={{ width: '150px' }}>
              <img
                src={photo}
                alt={`Thumbnail ${index + 1}`}
                style={{
                  width: '100%',
                  padding: '4px',
                  display: 'block',
                  borderRadius: '8px'
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </>
  );
}
